import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import {
  getCoachTeams,
  getClubSessions,
  getClubLocations,
  createSession,
  createSessionSeries,
  type ClubSession,
  type ClubLocation,
  type CoachTeam,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import DateTimeInput from "../../../components/DateTimeInput";
import { GameCard, GameHero, GameIcon, SegmentedControl } from "../../../components/game";
import DateInput from "../../../components/DateInput";
import SessionEditDialog from "../../../components/sessions/SessionEditDialog";

type SessionMode = "one_time" | "recurring";

interface SessionForm {
  mode: SessionMode;
  team_id: string;
  type: "sport" | "educational";
  title: string;
  // One-time
  start_at: string;
  end_at: string;
  // Recurring
  rec_start_date: string; // yyyy-mm-dd
  rec_start_time: string; // HH:mm
  rec_end_time: string;   // HH:mm
  weekdays: number[];     // 0=Sun..6=Sat (matches Date.getDay)
  /** How the series ends: after a number of weeks, or on a date. */
  rec_end_mode: "weeks" | "until";
  weeks: number;
  rec_until: string;      // yyyy-mm-dd
  // Location (shared)
  location_id: string;
  location_text: string;
}

const defaultForm: SessionForm = {
  mode: "one_time",
  team_id: "",
  type: "sport",
  title: "",
  start_at: "",
  end_at: "",
  rec_start_date: "",
  rec_start_time: "17:00",
  rec_end_time: "18:30",
  weekdays: [],
  rec_end_mode: "weeks",
  weeks: 8,
  rec_until: "",
  location_id: "",
  location_text: "",
};

// Mirrors the club-admin ClubSessions modal so the UX stays
// consistent. 0=Sun matches JS Date.getDay() — the backend's
// /sessions/series endpoint expects that convention.
const WEEKDAY_LABELS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Lu", long: "Luni" },
  { value: 2, short: "Ma", long: "Marți" },
  { value: 3, short: "Mi", long: "Miercuri" },
  { value: 4, short: "Jo", long: "Joi" },
  { value: 5, short: "Vi", long: "Vineri" },
  { value: 6, short: "Sâ", long: "Sâmbătă" },
  { value: 0, short: "Du", long: "Duminică" },
];

export default function AppCoachSessions() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [locations, setLocations] = useState<ClubLocation[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinct from `error` below (which is form-validation copy) — this flags a
  // failed initial data load so we can show a retry instead of an empty list.
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(searchParams.get("action") === "new");
  // Pre-fill team_id from URL param if present
  const teamParam = searchParams.get("team");
  const [form, setForm] = useState<SessionForm>({
    ...defaultForm,
    team_id: teamParam || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"upcoming" | "past" | "all">("upcoming");
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [calendarDate, setCalendarDate] = useState(new Date());

  const activeClubId = ctxClubId || ctxClubs[0]?.id;

  /** Which session the edit dialog is open on. */
  const [editing, setEditing] = useState<ClubSession | null>(null);

  const loadData = useCallback(async () => {
    if (!activeClubId || !user?.id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [sessionsData, coachTeamsData, locationsData] = await Promise.all([
        // Backend now scopes /sessions to the caller's coach teams
        // automatically (a coach without club_admin role only gets
        // their own sessions back). The client-side `filter()` we
        // used to do here is no longer needed AND it was a security
        // leak — DevTools could see every coach's schedule.
        getClubSessions(activeClubId),
        getCoachTeams(user.id),
        getClubLocations(activeClubId),
      ]);

      setSessions(sessionsData);
      setTeams(coachTeamsData.map((t: CoachTeam) => ({ id: t.id, name: t.name })));
      setLocations(locationsData);
    } catch (error) {
      console.error("Failed to load sessions:", error);
      setLoadError(true);
    }
    setLoading(false);
  }, [activeClubId, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter sessions
  const now = new Date();
  const filteredSessions = sessions.filter(s => {
    const sessionDate = new Date(s.start_at);
    if (filter === "upcoming") return sessionDate >= now;
    if (filter === "past") return sessionDate < now;
    return true;
  }).sort((a, b) => {
    const dateA = new Date(a.start_at).getTime();
    const dateB = new Date(b.start_at).getTime();
    return filter === "past" ? dateB - dateA : dateA - dateB;
  });

  const toggleWeekday = (wd: number) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(wd) ? f.weekdays.filter((x) => x !== wd) : [...f.weekdays, wd],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Branch on mode so the same modal serves both flows like the
    // club-admin ClubSessions modal does.
    if (form.mode === "one_time") {
      if (!form.team_id || !form.title || !form.start_at || !form.end_at) {
        setError(t("coach.fillAllFields", "Te rog completează toate câmpurile obligatorii"));
        return;
      }
    } else {
      if (
        !form.team_id ||
        !form.title ||
        !form.rec_start_date ||
        form.weekdays.length === 0 ||
        !form.rec_start_time ||
        !form.rec_end_time ||
        (form.rec_end_mode === "weeks" ? !form.weeks : !form.rec_until)
      ) {
        setError(t("coach.fillAllFields", "Te rog completează toate câmpurile obligatorii"));
        return;
      }
      if (form.rec_end_time <= form.rec_start_time) {
        setError(t("coach.endBeforeStart", "Ora de sfârșit trebuie să fie după cea de început"));
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      if (form.mode === "one_time") {
        const payload: any = {
          team_id: form.team_id,
          type: form.type,
          title: form.title,
          start_at: new Date(form.start_at).toISOString(),
          end_at: new Date(form.end_at).toISOString(),
        };
        if (form.location_id) payload.location_id = form.location_id;
        if (form.location_text) payload.location_text = form.location_text;
        const newSession = await createSession(payload);
        setSessions((prev) => [...prev, newSession]);
      } else {
        // Recurring: build the same payload shape the club-admin modal
        // sends to POST /sessions/series. The endpoint expands the
        // (weekdays × weeks) grid into individual sessions server-side.
        const seriesPayload: any = {
          team_id: form.team_id,
          type: form.type,
          title: form.title,
          // The /series endpoint also expects start_at + end_at — it
          // uses the time-of-day from these, paired with rec_start_date
          // / weeks to generate all occurrences. We assemble matching
          // ISO timestamps from the rec_* fields.
          start_at: new Date(`${form.rec_start_date}T${form.rec_start_time}`).toISOString(),
          end_at: new Date(`${form.rec_start_date}T${form.rec_end_time}`).toISOString(),
          weekdays: form.weekdays,
          // One or the other — see the club-admin modal.
          ...(form.rec_end_mode === "until" && form.rec_until
            ? { until: new Date(`${form.rec_until}T23:59:59`).toISOString() }
            : { weeks: form.weeks }),
        };
        if (form.location_id) seriesPayload.location_id = form.location_id;
        if (form.location_text) seriesPayload.location_text = form.location_text;
        const result = await createSessionSeries(seriesPayload);
        if (Array.isArray(result.sessions)) {
          setSessions((prev) => [...prev, ...(result.sessions as ClubSession[])]);
        }
      }

      setForm(defaultForm);
      setShowForm(false);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("action");
      newParams.delete("team");
      setSearchParams(newParams);
    } catch (err: any) {
      console.error("Failed to create session(s):", err);
      setError(err.response?.data?.error || t("coach.sessionCreateError", "Nu s-a putut crea sesiunea"));
    }
    setSaving(false);
  };

  // Live preview of when the recurring series will end, for both ways of
  // ending it. Mirrors the backend's expansion: for each picked weekday find
  // the first occurrence on/after rec_start_date and step weekly, stopping
  // after `weeks` steps or at the end date. Occurrences are counted rather
  // than multiplied, because with an end date two weekdays rarely get the
  // same number of slots.
  const recurringEndPreview = (() => {
    if (form.mode !== "recurring") return null;
    if (!form.rec_start_date || form.weekdays.length === 0) return null;
    const byWeeks = form.rec_end_mode === "weeks";
    if (byWeeks && !form.weeks) return null;
    if (!byWeeks && !form.rec_until) return null;

    const start = new Date(form.rec_start_date + "T00:00:00");
    if (isNaN(start.getTime())) return null;

    let until: Date | null = null;
    if (!byWeeks) {
      until = new Date(form.rec_until + "T23:59:59");
      if (isNaN(until.getTime()) || until < start) return null;
    }

    const sortedWeekdays = [...form.weekdays].sort((a, b) => a - b);
    const span = byWeeks
      ? form.weeks
      : Math.min(52, Math.floor((until!.getTime() - start.getTime()) / (7 * 86400000)) + 1);

    let latest: Date | null = null;
    let count = 0;
    for (const wd of sortedWeekdays) {
      const first = new Date(start);
      first.setDate(first.getDate() + ((wd - first.getDay() + 7) % 7));
      for (let w = 0; w < span; w++) {
        const occ = new Date(first);
        occ.setDate(occ.getDate() + 7 * w);
        if (until && occ > until) break;
        count++;
        if (!latest || occ > latest) latest = occ;
      }
    }
    return latest && count > 0 ? { date: latest, count } : null;
  })();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ro-RO", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
  };

  // Calendar helpers
  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0

    const days: (Date | null)[] = [];
    // Add empty slots for days before the first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getSessionsForDate = (date: Date) => {
    const dateStr = date.toISOString().split("T")[0];
    return sessions.filter(s => s.start_at.startsWith(dateStr));
  };

  const calendarDays = getMonthDays(calendarDate);

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{t("coach.sessions", "Sesiuni")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{t("coach.sessionsSubtitle", "Programează și gestionează antrenamentele")}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* View Mode Switch */}
              <div className="flex bg-white/15 rounded-2xl p-1">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-xl transition-colors ${
                    viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-white/70 hover:text-white"
                  }`}
                  title={t("coach.listView", "Vizualizare listă")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("calendar")}
                  className={`p-2 rounded-xl transition-colors ${
                    viewMode === "calendar" ? "bg-white text-slate-800 shadow-sm" : "text-white/70 hover:text-white"
                  }`}
                  title={t("coach.calendarView", "Vizualizare calendar")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              {/* Add Session Button */}
              <button
                onClick={() => {
                  setShowForm(true);
                  setSearchParams({ action: "new" });
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl font-bold text-[13px] transition-colors shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t("coach.add", "Adaugă")}
              </button>
            </div>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
        {/* Filter Tabs - Only show in list mode */}
        {viewMode === "list" && (
          <SegmentedControl<"upcoming" | "past" | "all">
            className="flex w-full [&>button]:flex-1"
            options={[
              { value: "upcoming", label: t("coach.upcoming", "Viitoare") },
              { value: "past", label: t("coach.past", "Trecute") },
              { value: "all", label: t("coach.all", "Toate") },
            ]}
            value={filter}
            onChange={setFilter}
          />
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : loadError ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : viewMode === "list" ? (
          /* List View */
          filteredSessions.length === 0 ? (
            <GameCard className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                <GameIcon name="clock" className="w-8 h-8" />
              </div>
              <p className="text-slate-500">{t("coach.noSessions", "Nu există sesiuni")}</p>
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 px-4 py-2.5 rounded-2xl text-sm font-extrabold text-white bg-gradient-to-b from-indigo-500 to-blue-600 shadow-[0_4px_0_#3730a3] active:shadow-[0_1px_0_#3730a3] active:translate-y-[3px] transition-transform duration-75"
              >
                {t("coach.createFirst", "Creează prima sesiune")}
              </button>
            </GameCard>
          ) : (
            <div className="space-y-3">
              {filteredSessions.map(session => (
                <Link
                  key={session.id}
                  to={`/app/coach/attendance?session=${session.id}`}
                  className="block bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] hover:border-indigo-200 active:translate-y-[2px] transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center text-white shadow-lg ${
                      new Date(session.start_at) >= now
                        ? "bg-gradient-to-br from-indigo-500 to-blue-600"
                        : "bg-gradient-to-br from-slate-400 to-slate-500"
                    }`}>
                      <span className="text-lg font-bold leading-none">
                        {new Date(session.start_at).getDate()}
                      </span>
                      <span className="text-xs uppercase">
                        {new Date(session.start_at).toLocaleString("ro-RO", { month: "short" })}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{session.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatTime(session.start_at)} - {formatTime(session.end_at)}</span>
                      </div>
                      {session.team?.name && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="truncate">{session.team.name}</span>
                        </div>
                      )}
                      {session.location_text && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span className="truncate">{session.location_text}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"
                      }`}>
                        {session.type === "sport" ? "Sport" : "Educațional"}
                      </span>
                      {(session.is_recurring || session.recurring_parent_id) && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700">
                          {t("coach.recurringBadge", "recurent")}
                        </span>
                      )}
                      {session._count && session._count.attendance > 0 && (
                        <span className="text-xs text-slate-400">
                          {session._count.attendance} prezențe
                        </span>
                      )}
                      {/* The card is a link into attendance, so the edit has
                          to stop the navigation itself. This button is the
                          only way a coach could change a training at all —
                          recurring ones included, which is the whole point. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditing(session);
                        }}
                        className="px-2.5 py-1 text-[11px] font-bold border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        {t("common.edit", "Modifică")}
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        ) : (
          /* Calendar View */
          <GameCard className="overflow-hidden">
            {/* Month Navigation */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
              <button
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="text-center">
                <span className="font-semibold text-slate-800">
                  {calendarDate.toLocaleDateString("ro-RO", { month: "long", year: "numeric" })}
                </span>
                <button
                  onClick={() => setCalendarDate(new Date())}
                  className="block mx-auto text-xs text-indigo-600 font-bold hover:text-indigo-700"
                >
                  {t("coach.today", "Azi")}
                </button>
              </div>
              <button
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100">
              {["L", "M", "M", "J", "V", "S", "D"].map((day, i) => (
                <div key={i} className="py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wide">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, index) => {
                if (!day) {
                  return <div key={index} className="min-h-[5rem] bg-slate-100 border-b border-r border-slate-200"></div>;
                }
                const daySessions = getSessionsForDate(day);
                const isToday = day.toDateString() === new Date().toDateString();
                const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));

                return (
                  <div
                    key={index}
                    className={`min-h-[5rem] border-b border-r border-slate-200 p-1.5 ${isPast ? "bg-slate-100" : "bg-white"}`}
                  >
                    <div className={`text-sm font-semibold mb-1.5 ${
                      isToday
                        ? "w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-md"
                        : isPast ? "text-slate-400" : "text-slate-800"
                    }`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1 overflow-hidden">
                      {daySessions.slice(0, 2).map(session => (
                        <Link
                          key={session.id}
                          to={`/app/coach/attendance?session=${session.id}`}
                          className={`block text-[11px] px-1.5 py-1 rounded font-medium truncate border ${
                            session.type === "sport"
                              ? "bg-emerald-200 text-emerald-800 border-emerald-300"
                              : "bg-purple-200 text-purple-800 border-purple-300"
                          }`}
                        >
                          {formatTime(session.start_at)} {session.title}
                        </Link>
                      ))}
                      {daySessions.length > 2 && (
                        <div className="text-[11px] text-slate-600 font-medium px-1">
                          +{daySessions.length - 2} {t("coach.more", "mai mult")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </GameCard>
        )}

        </div>

        {/* Create Session Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 !mt-0">
            <div
              className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col"
              style={{ maxHeight: "100%" }}
            >
              {/* Fixed Header */}
              <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">{t("coach.newSession", "Sesiune nouă")}</h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    // Clear all form-related params
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("action");
                    newParams.delete("team");
                    setSearchParams(newParams);
                    setForm(defaultForm);
                    setError("");
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5">
                <form id="create-session-form" onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  {/* Mode toggle — one-time vs recurring. Mirrors the
                      club-admin "Sesiune unică / Sesiune recurentă"
                      buttons so coaches get the same flow. */}
                  <SegmentedControl<SessionMode>
                    className="flex w-full [&>button]:flex-1"
                    options={[
                      { value: "one_time", label: t("coach.sessionOneTime", "Sesiune unică") },
                      { value: "recurring", label: t("coach.sessionRecurring", "Sesiune recurentă") },
                    ]}
                    value={form.mode}
                    onChange={(mode) => setForm({ ...form, mode })}
                  />

                  {/* Team */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.team", "Echipa")} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={form.team_id}
                        onChange={e => setForm({ ...form, team_id: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none cursor-pointer"
                        required
                      >
                        <option value="">{t("coach.selectTeam", "Selectează echipa")}</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.type", "Tip")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, type: "sport" })}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                          form.type === "sport"
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        Sport
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, type: "educational" })}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                          form.type === "educational"
                            ? "bg-purple-500 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        Educațional
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.title", "Titlu")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={e => setForm({ ...form, title: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t("coach.sessionTitlePlaceholder", "ex: Antrenament tehnic")}
                      required
                    />
                  </div>

                  {form.mode === "one_time" ? (
                    <>
                      {/* Start Date/Time */}
                      <DateTimeInput
                        value={form.start_at}
                        onChange={(value) => setForm({ ...form, start_at: value })}
                        label={t("coach.startAt", "Data și ora de început")}
                        placeholder={t("coach.selectStartDateTime", "Selectează data și ora de început")}
                        required
                      />

                      {/* End Date/Time */}
                      <DateTimeInput
                        value={form.end_at}
                        onChange={(value) => setForm({ ...form, end_at: value })}
                        label={t("coach.endAt", "Data și ora de sfârșit")}
                        placeholder={t("coach.selectEndDateTime", "Selectează data și ora de sfârșit")}
                        required
                        minDate={form.start_at}
                      />
                    </>
                  ) : (
                    /* Recurring config — weekdays + time-of-day pair +
                       start-date + nr weeks. Backend POST /sessions/series
                       expands this into one session per (weekday × week). */
                    <div className="space-y-4 p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t("coach.recurringWeekdays", "Zilele săptămânii")} <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-7 gap-1.5">
                          {WEEKDAY_LABELS.map((d) => {
                            const active = form.weekdays.includes(d.value);
                            return (
                              <button
                                key={d.value}
                                type="button"
                                onClick={() => toggleWeekday(d.value)}
                                title={d.long}
                                className={`py-2 rounded-lg text-sm font-bold transition-colors ${
                                  active
                                    ? "bg-indigo-600 text-white"
                                    : "bg-white text-slate-600 border border-slate-200 hover:border-indigo-300"
                                }`}
                              >
                                {d.short}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t("coach.timeFrom", "De la")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="time"
                            value={form.rec_start_time}
                            onChange={(e) => setForm({ ...form, rec_start_time: e.target.value })}
                            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t("coach.timeTo", "Până la")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="time"
                            value={form.rec_end_time}
                            onChange={(e) => setForm({ ...form, rec_end_time: e.target.value })}
                            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t("coach.recurringStartDate", "Data de început")} <span className="text-red-500">*</span>
                          </label>
                          <DateInput
                            value={form.rec_start_date}
                            onChange={(v) => setForm({ ...form, rec_start_date: v })}
                            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          {/* A season ends on a date; a block of training ends
                              after N weeks. Both are normal. */}
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t("coach.recurringEndMode", "Seria se termină")} <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-1 p-1 mb-2 rounded-xl bg-slate-100">
                            {([
                              { value: "weeks" as const, label: t("coach.recurringByWeeks", "Săptămâni") },
                              { value: "until" as const, label: t("coach.recurringByDate", "La o dată") },
                            ]).map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setForm({ ...form, rec_end_mode: opt.value })}
                                className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                  form.rec_end_mode === opt.value
                                    ? "bg-white text-indigo-700 shadow-sm"
                                    : "text-slate-500"
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {form.rec_end_mode === "weeks" ? (
                            <input
                              type="number"
                              min={1}
                              max={52}
                              value={form.weeks}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  weeks: Math.max(1, Math.min(52, parseInt(e.target.value || "1", 10))),
                                })
                              }
                              aria-label={t("coach.recurringWeeks", "Săptămâni")}
                              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          ) : (
                            <DateInput
                              value={form.rec_until}
                              onChange={(v) => setForm({ ...form, rec_until: v })}
                              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          )}
                        </div>
                      </div>

                      {recurringEndPreview && (
                        <div className="p-3 bg-white border border-indigo-200 rounded-lg">
                          <p className="text-xs text-indigo-700 font-medium">
                            {t("coach.recurringEndsOn", "Seria se termină pe")}{" "}
                            <span className="font-bold">
                              {recurringEndPreview.date.toLocaleDateString("ro-RO", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {t("coach.recurringTotalSessions", "Total estimat: {{count}} sesiuni", {
                              count: recurringEndPreview.count,
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.location", "Locație")}
                    </label>
                    {locations.length > 0 ? (
                      <div className="relative">
                        <select
                          value={form.location_id}
                          onChange={e => {
                            const loc = locations.find(l => l.id === e.target.value);
                            setForm({
                              ...form,
                              location_id: e.target.value,
                              location_text: loc?.name || "",
                            });
                          }}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none cursor-pointer"
                        >
                          <option value="">{t("coach.selectLocation", "Selectează locația")}</option>
                          {locations.map(loc => (
                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={form.location_text}
                        onChange={e => setForm({ ...form, location_text: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t("coach.locationPlaceholder", "ex: Sala de sport")}
                      />
                    )}
                  </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div className="flex-shrink-0 px-5 py-4 border-t border-slate-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    // Clear all form-related params
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("action");
                    newParams.delete("team");
                    setSearchParams(newParams);
                    setForm(defaultForm);
                    setError("");
                  }}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  type="submit"
                  form="create-session-form"
                  disabled={saving}
                  className="flex-1 py-3 rounded-2xl font-extrabold text-white bg-gradient-to-b from-indigo-500 to-blue-600 shadow-[0_4px_0_#3730a3] active:shadow-[0_1px_0_#3730a3] active:translate-y-[3px] transition-transform duration-75 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t("common.saving", "Se salvează...")}
                    </>
                  ) : form.mode === "recurring" ? (
                    t("coach.createSeries", "Crează seria")
                  ) : (
                    t("coach.createSession", "Creează sesiunea")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {editing && (
          <SessionEditDialog
            session={editing}
            locations={locations}
            onClose={() => setEditing(null)}
            onChanged={loadData}
          />
        )}
      </div>
    </MobileAppLayout>
  );
}
